import { supabase } from "../lib/supabase";
import { getEarningsStateMeta } from "./marketplaceFinanceStates";

function isMissingTableError(error) {
  if (!error) return false;

  return (
    error.code === "PGRST204" ||
    error.code === "42P01" ||
    /relation .* does not exist/i.test(error.message || "") ||
    /column .* does not exist/i.test(error.message || "")
  );
}

export async function fetchWalletSnapshot(profileId) {
  if (!profileId) return null;

  const { data, error } = await supabase
    .from("wallet_balance_snapshots")
    .select("*")
    .eq("profile_id", profileId)
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    if (isMissingTableError(error)) return null;
    throw error;
  }

  return data || null;
}

export async function fetchEarningsTimelineForWorker(workerProfileId, limit = 20) {
  if (!workerProfileId) return [];

  const { data, error } = await supabase
    .from("earnings_state_timeline")
    .select("*")
    .eq("worker_profile_id", workerProfileId)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) {
    if (isMissingTableError(error)) return [];
    throw error;
  }

  return data || [];
}

export async function fetchEarningsTimelineForBookings(bookingIds = []) {
  if (!bookingIds.length) return [];

  const { data, error } = await supabase
    .from("earnings_state_timeline")
    .select("*")
    .in("service_booking_id", bookingIds)
    .order("created_at", { ascending: false });

  if (error) {
    if (isMissingTableError(error)) return [];
    throw error;
  }

  return data || [];
}

export async function fetchAdminMarketplaceSignals(limit = 12) {
  const [riskResult, payoutResult, recoveryResult, fraudCasesResult] = await Promise.all([
    supabase
      .from("risk_events")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(limit),
    supabase
      .from("payout_requests_v2")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(limit),
    supabase
      .from("worker_settlement_exposures")
      .select("*")
      .order("updated_at", { ascending: false })
      .limit(limit),
    supabase
      .from("settlement_fraud_cases")
      .select("*")
      .order("updated_at", { ascending: false })
      .limit(limit),
  ]);

  if (riskResult.error && !isMissingTableError(riskResult.error)) {
    throw riskResult.error;
  }

  if (payoutResult.error && !isMissingTableError(payoutResult.error)) {
    throw payoutResult.error;
  }

  if (recoveryResult.error && !isMissingTableError(recoveryResult.error)) {
    throw recoveryResult.error;
  }

  if (fraudCasesResult.error && !isMissingTableError(fraudCasesResult.error)) {
    throw fraudCasesResult.error;
  }

  return {
    riskEvents: riskResult.data || [],
    payoutRequests: payoutResult.data || [],
    recoveryExposures: recoveryResult.data || [],
    fraudCases: fraudCasesResult.data || [],
  };
}

export async function fetchMarketplaceCapabilities() {
  const { data, error } = await supabase.rpc("mb_marketplace_capabilities");

  if (error) {
    if (isMissingTableError(error)) return null;
    return null;
  }

  return data || null;
}

export async function updateServiceBookingLifecycle({
  bookingId,
  workerProfileId,
  status,
  actorProfileId,
  visibleAmount = 0,
}) {
  const assignmentStatus =
    status === "accepted"
      ? "accepted"
      : status === "in_progress"
        ? "in_progress"
        : status === "completed"
          ? "completed"
          : null;

  const { error } = await supabase
    .from("service_bookings")
    .update({
      status,
      ...(assignmentStatus ? { assignment_status: assignmentStatus } : {}),
    })
    .eq("id", bookingId);

  if (error) throw error;

  if (!workerProfileId) {
    return { assignmentStatus, financeState: null };
  }

  try {
    if (status === "in_progress") {
      await supabase.rpc("mb_mark_work_started", {
        p_service_booking_id: bookingId,
        p_worker_profile_id: workerProfileId,
        p_actor_profile_id: actorProfileId || workerProfileId,
        p_visible_amount: Number(visibleAmount || 0),
        p_notes: "Lifecycle transition from dashboard.",
        p_metadata: { source: "dashboard_status_update", status },
      });

      return {
        assignmentStatus,
        financeState: "work_started",
      };
    }

    if (status === "completed") {
      await supabase.rpc("mb_mark_work_completed", {
        p_service_booking_id: bookingId,
        p_worker_profile_id: workerProfileId,
        p_actor_profile_id: actorProfileId || workerProfileId,
        p_visible_amount: Number(visibleAmount || 0),
        p_notes: "Completion submitted from dashboard.",
        p_metadata: { source: "dashboard_status_update", status },
      });

      return {
        assignmentStatus,
        financeState: "verification_hold",
      };
    }
  } catch (rpcError) {
    if (!isMissingTableError(rpcError)) {
      console.warn("Marketplace RPC update skipped:", rpcError);
    }
  }

  return { assignmentStatus, financeState: null };
}

export function getLatestTimelineEntry(timelineRows = []) {
  return timelineRows[0] || null;
}

export function formatMoney(amount) {
  return `Rs ${Number(amount || 0).toLocaleString("en-IN")}`;
}

export function getFinanceStateSummary(entry) {
  if (!entry?.state) return null;

  const meta = getEarningsStateMeta(entry.state);

  return {
    ...meta,
    state: entry.state,
    visibleAmount: Number(entry.visible_amount || 0),
    securedAmount: Number(entry.secured_amount || 0),
    withdrawableAmount: Number(entry.withdrawable_amount || 0),
    disputedAmount: Number(entry.disputed_amount || 0),
    recoveryAmount: Number(entry.recovery_amount || 0),
  };
}

export async function fetchWorkerRecoveryState(profileId) {
  if (!profileId) return null;

  const { data: worker, error: workerError } = await supabase
    .from("workers")
    .select(
      "id, profile_id, settlement_risk_tier, recovery_mode_status, max_unsecured_cash_exposure, current_settlement_due, recovery_hold_percent, cash_jobs_allowed, digital_priority_only, settlement_restriction_reason, settlement_last_reviewed_at, runaway_risk_score"
    )
    .eq("profile_id", profileId)
    .maybeSingle();

  if (workerError) {
    if (isMissingTableError(workerError)) return null;
    throw workerError;
  }

  if (!worker?.profile_id) {
    return null;
  }

  const { data: exposures, error: exposuresError } = await supabase
    .from("worker_settlement_exposures")
    .select("*")
    .eq("worker_profile_id", profileId)
    .order("updated_at", { ascending: false })
    .limit(10);

  if (exposuresError && !isMissingTableError(exposuresError)) {
    throw exposuresError;
  }

  return {
    ...worker,
    exposures: exposures || [],
  };
}

export function getRecoveryModeMeta(workerRecoveryState) {
  const mode = workerRecoveryState?.recovery_mode_status || "normal";

  const map = {
    normal: {
      label: "Normal",
      tone: "good",
      description: "Your account has no active recovery restrictions.",
    },
    watch: {
      label: "Watch",
      tone: "watch",
      description: "A settlement amount is open. Keep future jobs healthy to prevent restrictions.",
    },
    recovery: {
      label: "Recovery Mode",
      tone: "watch",
      description: "Part of future digital earnings may reduce your settlement due.",
    },
    cash_blocked: {
      label: "Cash Jobs Paused",
      tone: "risk",
      description: "Cash-risk jobs are paused until your settlement due reduces.",
    },
    suspended: {
      label: "Under Review",
      tone: "risk",
      description: "Your account needs an operations review before new work can start.",
    },
  };

  return map[mode] || map.normal;
}
